import React from 'react';
import { motion } from 'framer-motion';
import './App.css';

function App() {

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10
      }
    }
  };

  const floatingVariants = {
    float: {
      y: [-10, 10, -10],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const rotateVariants = {
    rotate: {
      rotate: [0, 5, -5, 0],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <div className="App">
      <motion.div
        className="container"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Hero Section */}
        <motion.section className="hero" variants={itemVariants}>
          <motion.div
            className="hero-image-container"
            variants={floatingVariants}
            animate="float"
            whileHover={{ scale: 1.1, rotate: 5 }}
            transition={{ type: "spring", stiffness: 200 }}
          >
            <img 
              src="/images/nemanja-color.png" 
              alt="Nemanja" 
              className="hero-image"
            />
            <motion.div
              className="hero-image-glow"
              animate={{
                boxShadow: [
                  '0 0 30px rgba(255, 217, 61, 0.5)',
                  '0 0 50px rgba(107, 207, 127, 0.5)',
                  '0 0 30px rgba(255, 217, 61, 0.5)'
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </motion.div>
          <motion.h1 
            className="title"
            variants={floatingVariants}
            animate="float"
          >
            Meet Nemanja!
          </motion.h1>
          <motion.p 
            className="subtitle"
            variants={itemVariants}
            style={{ fontSize: '1.8rem', fontWeight: '600' }}
          >
            The Most Cheerful Manager Who Redefined "Organized Chaos" 🚀
          </motion.p>
          <motion.p 
            className="subtitle-tagline"
            variants={itemVariants}
            style={{ 
              fontSize: '1.3rem', 
              fontWeight: '500',
              marginTop: '1rem',
              fontStyle: 'italic',
              opacity: 0.9
            }}
          >
            Nemanja NI (Natural Intelligence) or Low-Performing Devin
          </motion.p>
          <motion.div
            style={{ marginTop: '2rem', fontSize: '2rem' }}
            animate={{ 
              rotate: [0, 10, -10, 10, -10, 0],
              scale: [1, 1.1, 1, 1.1, 1]
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            👋 Hey there! 👋
          </motion.div>
        </motion.section>

        {/* Introduction */}
        <motion.section className="section" variants={itemVariants}>
          <motion.div 
            className="card card-with-image"
            whileHover={{ scale: 1.05, rotate: 1 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <div className="card-image-wrapper">
              <motion.img 
                src="/images/nemanja-color-laugh.png" 
                alt="Nemanja laughing"
                className="card-image"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              />
            </div>
            <h2>🎯 The Mission (Sort Of)</h2>
            <p>
              Nemanja runs an organization where Monday meetings are scheduled for 
              "sometime this week, probably," deadlines are "flexible suggestions," 
              and the project roadmap is written on sticky notes that keep falling off the wall.
            </p>
          </motion.div>
        </motion.section>

        {/* Daily Routine */}
        <motion.section className="section" variants={itemVariants}>
          <motion.div 
            className="card"
            whileHover={{ scale: 1.05, rotate: -1 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <h2>⏰ A Day in the Life</h2>
            <ul className="list">
              <motion.li
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                9:00 AM - Arrives "fashionably late" (his words, not HR's)
              </motion.li>
              <motion.li
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.7 }}
              >
                9:15 AM - Discovers yesterday's coffee is still in the mug
              </motion.li>
              <motion.li
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.9 }}
              >
                10:00 AM - Calls an "emergency meeting" to discuss lunch options
              </motion.li>
              <motion.li
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 1.1 }}
              >
                2:00 PM - Realizes the "urgent" project deadline was last week
              </motion.li>
              <motion.li
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 1.3 }}
              >
                5:00 PM - Declares "we'll finish tomorrow" (spoiler: they won't)
              </motion.li>
            </ul>
          </motion.div>
        </motion.section>

        {/* Management Style */}
        <motion.section className="section" variants={itemVariants}>
          <motion.div 
            className="card"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <h2>🎭 The Nemanja Management Method™</h2>
            <div className="method-grid">
              <motion.div 
                className="method-item"
                variants={rotateVariants}
                animate="rotate"
              >
                <h3>📋 Planning</h3>
                <p>"We'll figure it out as we go"</p>
              </motion.div>
              <motion.div 
                className="method-item"
                variants={rotateVariants}
                animate="rotate"
              >
                <h3>📊 Tracking</h3>
                <p>"It's in my head somewhere"</p>
              </motion.div>
              <motion.div 
                className="method-item"
                variants={rotateVariants}
                animate="rotate"
              >
                <h3>🎯 Goals</h3>
                <p>"Let's aim for... something"</p>
              </motion.div>
              <motion.div 
                className="method-item"
                variants={rotateVariants}
                animate="rotate"
              >
                <h3>📈 Metrics</h3>
                <p>"Trust me, it's going great"</p>
              </motion.div>
            </div>
          </motion.div>
        </motion.section>

        {/* Team Quotes */}
        <motion.section className="section" variants={itemVariants}>
          <motion.div 
            className="card card-with-image"
            whileHover={{ scale: 1.05, rotate: 1 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <div className="card-image-wrapper">
              <motion.img 
                src="/images/nemanja-bw.png" 
                alt="Nemanja"
                className="card-image card-image-bw"
                whileHover={{ scale: 1.1, rotate: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
              />
            </div>
            <h2>💬 What the Team Says</h2>
            <div className="quotes">
              <motion.blockquote
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                whileHover={{ scale: 1.05 }}
              >
                "Working with Nemanja is like navigating a maze blindfolded, 
                but somehow we always end up somewhere interesting."
              </motion.blockquote>
              <motion.blockquote
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                whileHover={{ scale: 1.05 }}
              >
                "His project timelines are more like suggestions written in invisible ink."
              </motion.blockquote>
              <motion.blockquote
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                whileHover={{ scale: 1.05 }}
              >
                "Nemanja's idea of 'organized' is having all his tabs open at once."
              </motion.blockquote>
            </div>
          </motion.div>
        </motion.section>

        {/* Achievements */}
        <motion.section className="section" variants={itemVariants}>
          <motion.div 
            className="card achievements"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <h2>🏆 Notable Achievements</h2>
            <div className="achievement-list">
              <motion.div
                className="achievement-item"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.5 }}
                whileHover={{ scale: 1.1, rotate: 5 }}
              >
                <span className="achievement-icon">🎉</span>
                <p>Successfully rescheduled the same meeting 47 times</p>
              </motion.div>
              <motion.div
                className="achievement-item"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.7 }}
                whileHover={{ scale: 1.1, rotate: -5 }}
              >
                <span className="achievement-icon">📧</span>
                <p>Maintains an inbox with 2,847 unread emails (and counting)</p>
              </motion.div>
              <motion.div
                className="achievement-item"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.9 }}
                whileHover={{ scale: 1.1, rotate: 5 }}
              >
                <span className="achievement-icon">🗂️</span>
                <p>Created a filing system so complex, only he understands it</p>
              </motion.div>
              <motion.div
                className="achievement-item"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 1.1 }}
                whileHover={{ scale: 1.1, rotate: -5 }}
              >
                <span className="achievement-icon">⏰</span>
                <p>Turned "ASAP" into a 3-week timeframe</p>
              </motion.div>
            </div>
          </motion.div>
        </motion.section>

        {/* Footer */}
        <motion.footer 
          className="footer"
          variants={itemVariants}
        >
          <motion.p
            variants={floatingVariants}
            animate="float"
          >
            Made with ❤️ (and a healthy dose of chaos) for Nemanja
          </motion.p>
          <p className="domain">nemanjat.org</p>
        </motion.footer>
      </motion.div>
    </div>
  );
}

export default App;
